// ================================================================================================
// React components
import * as React from "react";
import _ from "lodash";
import { saveAs } from "file-saver";

import {
    Card, CardHeader, CardText,
} from "material-ui";

// Icons
import ErrorIcon from 'material-ui/svg-icons/alert/error';
import RegisterIcon from 'material-ui/svg-icons/hardware/memory';

import { Macros } from '@ModelViews/ModelViews';
import {
    BoardMacroEngine, BoardMacroEngineLogInstance,
    MacroType, MacroInfoEx
} from "./BoardMacroEngine";
import { getZDepthShadow } from "@Utils/Colors";
import { isDefined, JSONSafeStringify } from "@Utils/Micro";
import { TypoLabel } from "@UI/Primitive/Typography";
import { RenderToolbar } from '@UI/Layout/AdvanceLayout';
import { ReactJsonView } from '@Primitive/ReactJsonView';
import { FolderItemType, FolderStructure } from '@Primitive/TreeStructure';
import { IComponentSizeProp } from '@Primitive/Interfaces';
import { TreeStructureLite } from '@Primitive/List/TreeStructureLite';
import { ToolbarIconButton } from '@Primitive/ToolbarIconButton';
import { GetGemTypeIcon } from "./BoardMacroUtils";

// ================================================================================================
export interface BoardMacroDbgViewProp extends React.Props<BoardMacroDbgView>, IComponentSizeProp {
    /**
     * The node key 
     */
    NodeKey: string;

    /**
     * Select guid that we need to open
     */
    onOpenEditor?: (macroGuid: string) => void
}
export interface BoardMacroDbgViewState {
    hideEarlyExitGems: boolean;
    SelectedMacroGuid: string;
    MacroFolderState: { [key: string]: boolean };
}
const enum GemsPagesKeys {
    Register = "__register__",
    Timeline = "__timeline__"
}
// ------------------------------------------------------------------------------------------------
export class BoardMacroDbgView extends React.Component<BoardMacroDbgViewProp, BoardMacroDbgViewState> {
    constructor(props: BoardMacroDbgViewProp) {
        super(props);
        this.state = {
            SelectedMacroGuid: GemsPagesKeys.Timeline,
            MacroFolderState: {},
            hideEarlyExitGems: true
        };
    }
    // --------------------------------------------------------------------------------------------
    renderResultsJS(log: BoardMacroEngineLogInstance) {
        return <React.Fragment>
            {
                (isDefined(log.Error) && log.Error.length > 0) ?
                    <React.Fragment>
                        <TypoLabel>Error:</TypoLabel>
                        <br />
                        <ReactJsonView
                            key={"JsHandlingResults"}
                            src={log.Error}
                            theme="monokai"
                            iconStyle="triangle"
                            displayDataTypes={false}
                            displayObjectSize={false}
                            collapseStringsAfterLength={40}
                            enableClipboard={true}
                            indentWidth={4}
                            collapsed={1}
                            style={{
                                margin: "4px 0px"
                            }}
                        />
                        <br />
                    </React.Fragment>
                    : null
            }
            <TypoLabel>JS Input:</TypoLabel>
            <br />
            <ReactJsonView
                key={"JSPrepInput"}
                src={log.JSPrepInput}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                collapseStringsAfterLength={40}
                enableClipboard={true}
                indentWidth={4}
                collapsed={1}
                style={{
                    margin: "4px 0px"
                }}
            />
            <TypoLabel style={{ marginTop: 15 }}>JS Results (Exec: {Math.round(log.Performance?.JSPrepExec)} Overall: {Math.round(log.Performance?.JSPrep)}ms):</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsPrepResults"}
                src={log.JsPrepResults}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                collapseStringsAfterLength={40}
                enableClipboard={true}
                indentWidth={4}
                collapsed={1}
                style={{
                    margin: "4px 0px"
                }}
            />
            <TypoLabel style={{ marginTop: 15 }}>Data Destination:</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsHandlingDst"}
                src={log.JsHandlingDst}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                collapseStringsAfterLength={40}
                enableClipboard={true}
                indentWidth={4}
                collapsed={1}
                style={{
                    margin: "4px 0px"
                }}
            />
        </React.Fragment>
    }
    // --------------------------------------------------------------------------------------------
    renderResultsQuery(log: BoardMacroEngineLogInstance) {
        return <React.Fragment>
            {
                (isDefined(log.Error) && log.Error.length > 0) ?
                    <React.Fragment>
                        <TypoLabel>Error:</TypoLabel>
                        <br />
                        <ReactJsonView
                            key={"JsHandlingResults"}
                            src={log.Error}
                            theme="monokai"
                            iconStyle="triangle"
                            displayDataTypes={false}
                            displayObjectSize={false}
                            collapseStringsAfterLength={40}
                            enableClipboard={true}
                            indentWidth={4}
                            collapsed={1}
                            style={{
                                margin: "4px 0px"
                            }}
                        />
                        <br />
                    </React.Fragment>
                    : null
            }
            <TypoLabel>JS Prep Input:</TypoLabel>
            <br />
            <ReactJsonView
                key={"JSPrepInput"}
                src={log.JSPrepInput}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                collapseStringsAfterLength={40}
                enableClipboard={true}
                indentWidth={4}
                collapsed={1}
                style={{
                    margin: "4px 0px"
                }}
            />
            <TypoLabel style={{ marginTop: 15 }}>JS Prep Results (Exec: {Math.round(log.Performance?.JSPrepExec)} Overall: {Math.round(log.Performance?.JSPrep)}ms):</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsPrepResults"}
                src={log.JsPrepResults}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                collapseStringsAfterLength={40}
                enableClipboard={true}
                indentWidth={4}
                collapsed={1}
                style={{
                    margin: "4px 0px"
                }}
            />
            <TypoLabel style={{ marginTop: 15 }}>Query Results ({Math.round(log.Performance?.Query)}ms):</TypoLabel>
            <br />
            <ReactJsonView
                key={"QueryResults"}
                src={log.QueryResults}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                collapseStringsAfterLength={40}
                enableClipboard={true}
                indentWidth={4}
                collapsed={1}
                style={{
                    margin: "4px 0px"
                }}
            />
            <TypoLabel style={{ marginTop: 15 }}>JS Handling Results (Exec: {Math.round(log.Performance?.JSHandlingExec)} Overall: {Math.round(log.Performance?.JSHandling)}ms):</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsHandlingResults"}
                src={log.JsHandlingResults}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                collapseStringsAfterLength={40}
                enableClipboard={true}
                indentWidth={4}
                collapsed={1}
                style={{
                    margin: "4px 0px"
                }}
            />
            <TypoLabel style={{ marginTop: 15 }}>Data Destination:</TypoLabel>
            <br />
            <ReactJsonView
                key={"JsHandlingDst"}
                src={log.JsHandlingDst}
                theme="monokai"
                iconStyle="triangle"
                displayDataTypes={false}
                displayObjectSize={false}
                collapseStringsAfterLength={40}
                enableClipboard={true}
                indentWidth={4}
                collapsed={1}
                style={{
                    margin: "4px 0px"
                }}
            />
        </React.Fragment>
    }
    // --------------------------------------------------------------------------------------------
    renderMacroTree() {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;
        let macros = engine.MacroDebugHistory;

        const pageSelectedStyle: React.CSSProperties = {
            color: "rgb(0, 157, 119)",
            fontWeight: 'bold',
            backgroundColor: "rgba(0,0,0,0.1)",
            borderLeft: "4px solid " + "rgb(0, 157, 119)"
        };

        // hide early exit executions
        let treeMacros = _.values(macros);
        if (this.state.hideEarlyExitGems)
            treeMacros = treeMacros.filter(x => _.findIndex(x.Log, l => l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0)) > -1);

        // create folders
        let structure = TreeStructureLite.helperPathSplitter(
            treeMacros,
            "Macro.Settings.Path",
            (log, path, id) => {
                let item = log.Macro;
                let iconUrl = GetGemTypeIcon(item.MacroType);

                let leftIcon = [<img src={iconUrl} width={24} height={24} />];
                if (item.Settings.Type == MacroType.Less)
                    leftIcon = [<i className="fab fa-less" style={{ paddingLeft: 10 }} />]
                else if (item.Settings.Type == MacroType.Init)
                    leftIcon.push(<i className="fas fa-meteor" style={{ paddingLeft: 10 }} />)

                return {
                    _id: id,
                    key: item.Guid,
                    path: path,
                    primaryText: item.Settings.Name,
                    secondaryText: item.Settings.Description,
                    parentId: 0,
                    type: FolderItemType.Dashboard,
                    children: [],
                    style: (this.state.SelectedMacroGuid == item.Guid) ? pageSelectedStyle : {},
                    leftIcon: leftIcon,
                    rightIcon: <div style={{
                        marginRight: 8, marginTop: 6,
                        borderRadius: 24, width: 24, height: 24,
                        backgroundColor: log.LastStateHaveErrors ? "#e53935" : "#039BE5",
                        textAlign: "center",
                        fontSize: 16, color: "white"
                    }}>
                        {log.Log.length}
                    </div>
                } as FolderStructure
            },
            (filename, path, id) => {
                let isOpen = this.state.MacroFolderState[path];
                if (!isDefined(isOpen)) {
                    this.state.MacroFolderState[path] = true;
                    isOpen = true;
                }

                return {
                    _id: id, children: [], path: path, key: path,
                    type: FolderItemType.Folder, primaryText: filename, parentId: 0,
                    leftFontIcon: (isOpen) ? ["fa-folder-open"] : ["fa-folder"],
                    open: isOpen,
                };
            });

        structure.unshift({
            _id: 10000,
            key: GemsPagesKeys.Timeline,
            path: "",
            primaryText: "Timeline",
            parentId: 0,
            type: FolderItemType.Dashboard,
            children: [],
            style: (this.state.SelectedMacroGuid == GemsPagesKeys.Timeline) ? pageSelectedStyle : {},
            leftIcon: <i className="fas fa-list-alt" style={{ color: "#73b129" }} />,
        });

        structure.unshift({
            _id: 10000,
            key: GemsPagesKeys.Register,
            path: "",
            primaryText: "Register",
            parentId: 0,
            type: FolderItemType.Dashboard,
            children: [],
            style: (this.state.SelectedMacroGuid == GemsPagesKeys.Register) ? pageSelectedStyle : {},
            leftIcon: <RegisterIcon color="#039BE5" />,
        });

        return <TreeStructureLite
            secondaryTextLimitLine={2}
            FolderStructure={structure}
            onListItemSelected={(key) => this.setState({
                SelectedMacroGuid: key
            })}
            onNestedListToggle={(id: number, key: string) => {
                this.setState((s) => {
                    if (!_.has(s.MacroFolderState, key)) {
                        s.MacroFolderState[key] = true;
                    } else {
                        s.MacroFolderState[key] = !s.MacroFolderState[key];
                    }
                    return s;
                });
            }}
        />
    }
    // --------------------------------------------------------------------------------------------
    renderTimeline() {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;


        let macrosLogs = _(engine.MacroDebugHistory)
            .filter(e => !this.state.hideEarlyExitGems ||
                (_.findIndex(e.Log, l => l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0)) > -1))
            .flatMap(e => {
                return _(e.Log)
                    .filter(l => !this.state.hideEarlyExitGems || l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0))
                    .map((l, index) => {
                        let nl: BoardMacroEngineLogInstance & { Macro?: MacroInfoEx, i?: number } = _.clone(l);
                        nl.Macro = e.Macro;
                        nl.i = index;
                        return nl;
                    })
                    .value();
            })
            .orderBy(x => x.Timestamp, "desc")
            .value();

        return <div>
            {
                _.map(macrosLogs, l => {
                    // Set useful flags.
                    const hasError: boolean = isDefined(l.Error) && l.Error.length > 0;

                    // extract icon 
                    let iconUrl = GetGemTypeIcon(l.Macro.MacroType);
                    let leftIcon = <img src={iconUrl} width={45} height={45} style={{ backgroundColor: "#262822", padding: 4, borderRadius: 10 }} />;

                    // Set card's header subtitle.
                    const subtitle: React.ReactNode = <div>
                        <div>{"Completion Timestamp: " + l.Timestamp}</div>
                        {
                            (l.Macro.Settings.Type == MacroType.Init) ?
                                <div>Init Gem</div> : null
                        }
                        {
                            (l.Macro.Settings.Type == MacroType.Less) ?
                                <div>Less styling Gem</div> : null
                        }
                        {
                            isDefined(l.Performance) ?
                                <div>{'Performance: ' + Math.round(l.Performance.JSHandling + l.Performance.Query + l.Performance.JSPrep) + "ms"}</div>
                                : null
                        }
                        {
                            isDefined(l.Performance) ?
                                <div>{'PerformanceExec: ' + Math.round(l.Performance.JSHandlingExec + l.Performance.Query + l.Performance.JSPrepExec) + "ms"}</div>
                                : null
                        }
                        {
                            (isDefined(l.Performance) && l.Performance.Query > 0) ?
                                <div>{'Query Time: ' + Math.round(l.Performance.Query) + "ms"}</div>
                                : null
                        }
                        {
                            _.map(l.Error,
                                e => <div>
                                    <ErrorIcon
                                        color={"#e53935"}
                                        style={{
                                            width: 18,
                                            height: 18,
                                            verticalAlign: 'sub'
                                        }} />
                                    {'[' + e.from + "] " + e.msg}
                                </div>)
                        }
                        {
                            (l.StoppedFromUser) ?
                                <div>Execution stopped from user.</div>
                                : null
                        }
                    </div>;

                    return <Card
                        key={l.Macro.Guid + " - " + l.i}
                        style={{
                            margin: 5,
                            borderRadius: 10,
                            backgroundColor: "rgba(255, 255, 255, 0.9)"
                        }} >
                        <CardHeader
                            title={l.Macro.Settings.Path + "/" + l.Macro.Settings.Name}
                            titleStyle={{ color: hasError ? "#da0500" : ((l.JsPrepResults != null) ? "#1964b9" : "#965e03"), fontWeight: 700 }}
                            subtitle={subtitle}
                            actAsExpander={true}
                            showExpandableButton={true}
                            avatar={leftIcon}
                        />
                        <CardText expandable={true}>
                            <RenderToolbar
                                height={35}
                                style={{ backgroundColor: "white", color: "black", boxShadow: "unset" }}
                                RightElements={[
                                    <ToolbarIconButton tooltip="Open Gem Editor" className="fas fa-laptop-code" onClick={() => this.openEditorSelected(l.Macro.Guid)} />
                                ]}
                            />
                            {(l.Macro.MacroType == Macros.MacroScriptType.Javascript) ?
                                this.renderResultsJS(l) :
                                this.renderResultsQuery(l)}
                        </CardText>
                    </Card>
                })
            }
        </div>
    }
    // --------------------------------------------------------------------------------------------
    renderSelectedMacro() {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;

        // Register
        if (this.state.SelectedMacroGuid == GemsPagesKeys.Register)
            return <div style={{ overflow: "hidden", borderRadius: 10, margin: 5 }}>
                <ReactJsonView
                    key={"json-viewer-output"}
                    src={engine.Register}
                    theme="monokai"
                    iconStyle="triangle"
                    displayDataTypes={false}
                    displayObjectSize={true}
                    collapseStringsAfterLength={40}
                    enableClipboard={true}
                    indentWidth={4}
                    collapsed={1} />
            </div>

        // Timeline
        if (this.state.SelectedMacroGuid == GemsPagesKeys.Timeline)
            return this.renderTimeline();

        let selected = _.find(engine.MacroDebugHistory,
            x => x.Macro.Guid == this.state.SelectedMacroGuid &&
                (!this.state.hideEarlyExitGems ||
                    _.findIndex(x.Log, l => l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0)) > -1));
        if (!isDefined(selected))
            return null;

        return _(selected.Log)
            .filter(l => !this.state.hideEarlyExitGems || l.JsPrepResults != null || (isDefined(l.Error) && l.Error.length > 0))
            .map(l => {
                // Set useful flags.
                const hasError: boolean = isDefined(l.Error) && l.Error.length > 0;

                // Set card's header subtitle.
                const subtitle: React.ReactNode = <div>
                    {
                        isDefined(selected.Log[0].Performance) ?
                            <div>{'Performance: ' + Math.round(_.sum(_.values(selected.Log[0].Performance))) + "ms"}</div>
                            : null
                    }
                    {
                        _.map(l.Error,
                            e => <div>
                                <ErrorIcon
                                    color={"#e53935"}
                                    style={{
                                        width: 18,
                                        height: 18,
                                        verticalAlign: 'sub'
                                    }} />
                                {'[' + e.from + "] " + e.msg}
                            </div>)
                    }
                </div>;

                return <Card style={{
                    margin: 5,
                    borderRadius: 10,
                    backgroundColor: "rgba(255, 255, 255, 0.9)"
                }} key={l.Timestamp}>
                    <CardHeader
                        title={l.Timestamp}
                        titleStyle={{ color: hasError ? "#da0500" : ((l.JsPrepResults != null) ? "#1964b9" : "#965e03"), fontWeight: 700 }}
                        subtitle={subtitle}
                        actAsExpander={true}
                        showExpandableButton={true}
                    />
                    <CardText expandable={true}>
                        {(selected.Macro.MacroType == Macros.MacroScriptType.Javascript) ?
                            this.renderResultsJS(l) :
                            this.renderResultsQuery(l)}
                    </CardText>
                </Card>
            })
            .value();
    }
    // --------------------------------------------------------------------------------------------
    clearLogs = () => {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;
        engine.MacroDebugHistory = {};
        this.forceUpdate();
    }
    // --------------------------------------------------------------------------------------------
    downloadLogs = () => {
        let engine = BoardMacroEngine.GetInstance(this.props.NodeKey);
        if (!isDefined(engine))
            return null;
        let logs = {};
        _.forEach(engine.MacroDebugHistory, (h, key) => {
            logs[key] = {
                Type: h.Macro.MacroType,
                Name: h.Macro.Settings.Name,
                Path: h.Macro.Settings.Path,
                Logs: h.Log
            };
        });
        let data = JSONSafeStringify(logs, undefined, "\t");
        saveAs(new Blob([data], { type: "text/plain;charset=utf-8" }), "MacroLog.json");
    }
    // --------------------------------------------------------------------------------------------
    openEditor = () => this.openEditorSelected(this.state.SelectedMacroGuid)
    // --------------------------------------------------------------------------------------------
    openEditorSelected = (guid) => {
        if (guid != null &&
            guid != GemsPagesKeys.Timeline &&
            guid != GemsPagesKeys.Register &&
            this.props.onOpenEditor != null)
            this.props.onOpenEditor(guid);
    }
    // --------------------------------------------------------------------------------------------
    hideEarlyExitExecutions = () => {
        this.setState({ hideEarlyExitGems: !this.state.hideEarlyExitGems })
    }
    // --------------------------------------------------------------------------------------------
    render() {
        let bodyHeight = this.props.height - 35;
        return <React.Fragment>
            <RenderToolbar
                height={35}
                style={{ backgroundColor: "rgb(38, 40, 34)" }}
                RightElements={[
                    (this.state.SelectedMacroGuid != null &&
                        this.state.SelectedMacroGuid != GemsPagesKeys.Timeline &&
                        this.state.SelectedMacroGuid != GemsPagesKeys.Register) ?
                        <ToolbarIconButton tooltip="Open Gem Editor" className="fas fa-laptop-code" onClick={this.openEditor} /> : null,
                    <ToolbarIconButton
                        tooltip={this.state.hideEarlyExitGems ? "Show all executed gems" : "Hide early exit execution gems"}
                        className={this.state.hideEarlyExitGems ? "fas fa-eye-slash" : "fas fa-eye"} onClick={this.hideEarlyExitExecutions} />,
                    <ToolbarIconButton tooltip="Clear Log" className="fas fa-trash" onClick={this.clearLogs} />,
                    <ToolbarIconButton tooltip="Download Log" className="fas fa-cloud-download-alt" onClick={this.downloadLogs} />
                ]}
                LeftElements={[
                ]}
            />
            <div style={{
                width: this.props.width,
                height: bodyHeight,
                display: "flex",
                backgroundColor: "rgb(38, 40, 34)",
            }}>
                <div style={{
                    height: bodyHeight,
                    width: 300,
                    color: "white",
                    overflow: "auto",
                    boxShadow: getZDepthShadow(3),
                    position: "relative"
                }}>
                    {this.renderMacroTree()}
                </div>
                <div style={{
                    flex: 1,
                    height: bodyHeight,
                    overflow: "hidden auto", backgroundColor: "rgb(234, 234, 233)"
                }}>
                    {this.renderSelectedMacro()}
                </div>
            </div>
        </React.Fragment>
    }
    // --------------------------------------------------------------------------------------------
}